/**
 * Preview Frame — Renders the generated website in an iframe with responsive sizing.
 * Supports inline editing mode: text is editable directly in the preview.
 */

import { useRef, useEffect } from 'react';

interface PreviewFrameProps {
  url: string | null;
  device: 'desktop' | 'tablet' | 'mobile';
  editMode?: boolean;
  selectedSection?: string | null;
  onSectionClick?: (sectionId: string) => void;
  /** Called when the user edits content inline in the preview */
  onHtmlEdit?: (html: string) => void;
  /** Called when user clicks an image in the preview */
  onImageClick?: (src: string) => void;
}

const DEVICE_SIZES = {
  desktop: { width: '100%', maxWidth: '100%' },
  tablet: { width: '768px', maxWidth: '768px' },
  mobile: { width: '375px', maxWidth: '375px' },
};

export function PreviewFrame({
  url,
  device,
  editMode = true,
  onHtmlEdit,
  onImageClick,
}: PreviewFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const size = DEVICE_SIZES[device];

  // Build the iframe URL — append ?edit=true for inline editing
  const iframeSrc = url
    ? editMode
      ? `${url}${url.includes('?') ? '&' : '?'}edit=true`
      : url
    : null;

  // Reload iframe when URL changes
  useEffect(() => {
    if (iframeRef.current && iframeSrc) {
      iframeRef.current.src = iframeSrc;
    }
  }, [iframeSrc]);

  // Listen for postMessage from editor bridge in iframe
  useEffect(() => {
    function handleMessage(e: MessageEvent) {
      if (!e.data || typeof e.data !== 'object') return;
      if (e.data.type === 'tyashin-editor-save' && e.data.html) {
        onHtmlEdit?.(e.data.html);
      }
      if (e.data.type === 'tyashin-editor-pick-image' && e.data.src) {
        onImageClick?.(e.data.src);
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onHtmlEdit, onImageClick]);

  if (!url) {
    return (
      <div
        className="flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white p-12"
        style={{ width: size.width, maxWidth: size.maxWidth, minHeight: '400px' }}
      >
        <p className="text-sm text-gray-400">
          Preview will appear here once generation is complete
        </p>
      </div>
    );
  }

  return (
    <div
      className="transition-all duration-300 ease-in-out"
      style={{ width: size.width, maxWidth: size.maxWidth }}
    >
      <div className="overflow-hidden rounded-lg border bg-white shadow-lg">
        {/* Browser chrome */}
        <div className="flex items-center gap-2 border-b bg-gray-50 px-3 py-2">
          <div className="flex gap-1.5">
            <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
            <div className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
            <div className="h-2.5 w-2.5 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 rounded-md border bg-white px-3 py-1 text-xs text-gray-400">
            {url
              .replace(/^https?:\/\//, '')
              .split('/')
              .slice(0, 2)
              .join('/')}
          </div>
          {editMode && (
            <div className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-600">
              Edit Mode
            </div>
          )}
        </div>

        {/* iframe */}
        <iframe
          ref={iframeRef}
          src={iframeSrc || undefined}
          className="w-full border-0"
          style={{ height: 'calc(100vh - 140px)', minHeight: '500px' }}
          title="Website Preview"
          sandbox="allow-scripts allow-same-origin"
        />
      </div>
    </div>
  );
}
